import { useIDTagging, useTranslation } from '@msdk/common'
import {
  RowActionBehaviour,
  SimpleConditionDefinition,
  TooltipDefinition,
  ViewColumnDetail,
  ViewPinnedDefinition,
} from '@msdk/definitions'
import {
  ArrowDownIcon,
  ArrowUpIcon,
  Box,
  ButtonIcon,
  Checkbox,
  COLORS,
  FilterIcon,
  IconSelector,
  Markdown,
  styled,
  TableCell,
  TableHeader,
  TableRow,
  Tooltip,
} from '@msdk/ui'
import React, { useEffect, useRef, useState } from 'react'

import { ColumnMenu } from './ColumnMenu'
import { ColumnHeaderInfo, CustomViewColumnDefinition, useDynamicViewContext } from './context'
import { ActionsTableCell, CheckboxTableCell, EmptyTableCell, StyledTableHeaderCell } from './utils'

const CellWrapper = styled(Box)`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
`

const StyledColumnLabel = styled('div')`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  position: relative;
`

const StyledColumnSubtitle = styled('div')`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  color: ${COLORS.text.secondary};
`

const FilterIconStyled = styled(FilterIcon)`
  color: ${COLORS.text.secondary};
  cursor: default;
`

const HeaderTableRow = styled(TableRow)`
  :hover {
    background: initial;
    cursor: default;
  }
`

const ColumnResizer = styled('slot', {
  shouldForwardProp: (prop) => prop !== '$isFirst' && prop !== '$pinned' && prop !== '$isLastColumn',
})<{
  $isFirst?: boolean
  $pinned?: ViewPinnedDefinition
  $isLastColumn?: boolean
}>`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  min-height: 32px;
  height: 100%;
  width: 6px;
  z-index: 5;

  background-color: transparent;

  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    width: ${({ $isFirst, $pinned }) => ($isFirst && $pinned === ViewPinnedDefinition.Left ? '0' : '2px')};
    min-height: 32px;
    background-color: ${COLORS.divider};
    display: block;
    border-radius: 6px;
  }

  &:hover {
    cursor: col-resize;

    &::after {
      width: 4px;
      background-color: ${COLORS.primary.main};
    }
  }
`

const SortButton = styled(ButtonIcon)`
  margin-right: 8px;
`

const InfoButton = styled(ButtonIcon)`
  & > svg {
    width: 16px;
    height: 16px;
  }
`

const StyledTableHeader = styled(TableHeader, {
  shouldForwardProp: (prop) => prop !== 'isColumnsLayoutInit' && prop !== 'shouldHideHeader',
})<{
  isColumnsLayoutInit?: boolean
  shouldHideHeader?: boolean
}>`
  ${({ isColumnsLayoutInit, shouldHideHeader }) =>
    !isColumnsLayoutInit || shouldHideHeader ? 'visibility: hidden' : ''};
  ${({ isColumnsLayoutInit, shouldHideHeader }) => (!isColumnsLayoutInit || shouldHideHeader ? 'height: 0' : '')};
`

export function TableHead({ isColumnsLayoutInit }: { isColumnsLayoutInit: boolean }) {
  const {
    additionalCondition,
    anyRowHasActions,
    handleOrderChange,
    hideHeader,
    isEmpty,
    multiSelect,
    onSelectAllRows,
    order,
    orderBy,
    rows,
    selectedRows,
    sortColumns,
    viewModel,
    visibleColumns,
    handleColumnResize,
    pinned,
    styleMode,
    showExtra,
    getColumnWidth,
    getFreeSpace,
    getColumnHeaderInfo,
  } = useDynamicViewContext()

  const t = useTranslation()
  const idTaggingEnabled = useIDTagging()

  const columnMenuAnchor = useRef<HTMLElement>(null)

  const resizeData = useRef({
    startX: 0,
    columnId: '',
    startWidth: 0,
    isResizing: false,
  })

  const [activeColumnMenu, setActiveColumnMenu] = useState<CustomViewColumnDefinition>()

  const isSortableColumn = (fieldName: string) => !!sortColumns.find((c) => c.fieldName === fieldName)

  const shouldHideHeader = hideHeader !== undefined ? hideHeader : viewModel.hideHeader

  const multiSel =
    !isEmpty && multiSelect
      ? {
          numSelected: selectedRows?.length ?? 0,
          onSelectAll: onSelectAllRows!,
          rowNumber: rows?.length ?? 0,
        }
      : undefined

  const onSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (multiSel) {
      multiSel.onSelectAll(event.target.checked)
    }
  }

  const hasFilter = (column: string) => {
    return additionalCondition?.conditions.some((c) => (c as SimpleConditionDefinition).field === column)
  }

  const handleResize = (e: MouseEvent) => {
    if (!resizeData.current.isResizing) return

    const { startX, columnId, startWidth } = resizeData.current
    const width = Math.max(0, startWidth + (e.clientX - startX))

    handleColumnResize(columnId, `${width}px`)
  }

  const endResize = () => {
    resizeData.current.isResizing = false
    document.removeEventListener('mousemove', handleResize)
    document.removeEventListener('mouseup', endResize)
  }

  const startResize = (e: React.MouseEvent, columnId: string) => {
    e.stopPropagation()
    e.preventDefault()

    const width = e.currentTarget.parentElement?.getBoundingClientRect().width

    if (width === undefined) {
      return
    }

    resizeData.current = {
      startX: e.clientX,
      columnId,
      startWidth: width,
      isResizing: true,
    }

    document.addEventListener('mousemove', handleResize)
    document.addEventListener('mouseup', endResize)
  }

  const isDetailView = viewModel.detailView

  const shouldShowActionsColumn = viewModel.rowActions.behaviour !== RowActionBehaviour.Hide

  const mode = styleMode ?? 'Default'

  function renderColumnTooltip(tooltip: TooltipDefinition) {
    return (
      <Tooltip title={<Markdown>{t(tooltip.idml, tooltip.description)}</Markdown>} arrow placement="bottom">
        <InfoButton size="small">
          <IconSelector iconName="InfoOutlined" color={COLORS.action.active} />
        </InfoButton>
      </Tooltip>
    )
  }

  const EllipsisLabel = (props: {
    headerInfo: ColumnHeaderInfo
    tooltip: TooltipDefinition | null
    $width?: string
  }) => {
    const textRef = useRef<HTMLDivElement>(null)
    const [isTruncated, setIsTruncated] = useState(false)
    const [isHover, setIsOver] = useState(false)

    useEffect(() => {
      if (!textRef.current) {
        return
      }
      setIsTruncated(textRef.current.clientWidth < textRef.current.scrollWidth)
    }, [isHover])

    return (
      <Tooltip
        title={props.headerInfo.title}
        open={isTruncated && isHover}
        slotProps={{
          tooltip: {
            sx: {
              fontSize: '11px',
              padding: '4px',
              '&.MuiTooltip-tooltip.MuiTooltip-tooltipPlacementBottom': {
                marginTop: 0,
              },
            },
          },
        }}
      >
        <>
          <StyledColumnLabel ref={textRef} onMouseEnter={() => setIsOver(true)} onMouseLeave={() => setIsOver(false)}>
            {props.headerInfo.title}
            {props.headerInfo.subtitle && <StyledColumnSubtitle>{props.headerInfo.subtitle}</StyledColumnSubtitle>}
          </StyledColumnLabel>
          {props.tooltip && renderColumnTooltip(props.tooltip)}
        </>
      </Tooltip>
    )
  }

  return (
    <StyledTableHeader
      isColumnsLayoutInit={isColumnsLayoutInit}
      shouldHideHeader={shouldHideHeader}
      ref={columnMenuAnchor as any}
    >
      {!shouldHideHeader && (
        <HeaderTableRow>
          {isDetailView && <TableCell data-col-name="DETAIL"></TableCell>}
          {!!multiSel && (
            <CheckboxTableCell data-col-name="SELECT" $visible>
              <Checkbox
                color="primary"
                indeterminate={multiSel.numSelected > 0 && multiSel.numSelected < multiSel.rowNumber}
                checked={multiSel.rowNumber > 0 && multiSel.numSelected === multiSel.rowNumber}
                onChange={onSelectAll}
              />
            </CheckboxTableCell>
          )}
          {visibleColumns.map((column, i) => (
            <StyledTableHeaderCell
              $idTaggingEnabled={idTaggingEnabled}
              data-col-name={column.fieldName}
              key={column.fieldName}
              $width={getColumnWidth(column.fieldName)}
              scope="col"
              size="small"
              $first={i === 0}
              $pinned={pinned}
              $isExtra={column.detail === ViewColumnDetail.Extra}
              $showButtons={activeColumnMenu === column}
              styleMode={mode}
              $visible={column.visibility && (column.detail !== ViewColumnDetail.Extra || showExtra)}
            >
              <CellWrapper>
                <EllipsisLabel
                  headerInfo={getColumnHeaderInfo(column)}
                  tooltip={column.tooltip}
                  $width={getColumnWidth(column.fieldName)}
                />
                {isSortableColumn(column.fieldName) && (
                  <SortButton
                    className={`sort-button ${orderBy === column.fieldName ? 'sort-button--active' : ''}`}
                    size="small"
                    onClick={() => handleOrderChange(column.fieldName)}
                  >
                    {orderBy !== column.fieldName && <ArrowUpIcon fontSize="small" />}
                    {orderBy === column.fieldName && order == 'asc' && <ArrowUpIcon fontSize="small" />}
                    {orderBy === column.fieldName && order == 'desc' && <ArrowDownIcon fontSize="small" />}
                  </SortButton>
                )}
                {hasFilter(column.fieldName) && <FilterIconStyled fontSize="small" />}
                <ColumnMenu
                  columnDef={column}
                  menuAnchor={columnMenuAnchor}
                  onToggle={(isOpen) => {
                    setActiveColumnMenu(isOpen ? column : undefined)
                  }}
                />
              </CellWrapper>
              <ColumnResizer
                className="column-resizer"
                onMouseDown={(e) => startResize(e, column.fieldName)}
                $isFirst={i === 0}
                $pinned={pinned}
                $isLastColumn={!shouldShowActionsColumn && i === visibleColumns.length - 1}
              />
            </StyledTableHeaderCell>
          ))}
          <EmptyTableCell data-col-name="EMPTY" $width={getFreeSpace()} $visible />
          {shouldShowActionsColumn && (
            <ActionsTableCell
              data-col-name="ACTIONS"
              shouldShow={anyRowHasActions || rows?.length === 0}
              styleMode={styleMode}
            />
          )}
        </HeaderTableRow>
      )}
      {shouldHideHeader && !!multiSel && (
        //in case of binned item active whe need an header for the check all button
        <HeaderTableRow>
          {isDetailView && <TableCell></TableCell>}
          <Checkbox
            color="primary"
            indeterminate={multiSel.numSelected > 0 && multiSel.numSelected < multiSel.rowNumber}
            checked={multiSel.rowNumber > 0 && multiSel.numSelected === multiSel.rowNumber}
            onChange={onSelectAll}
          />
        </HeaderTableRow>
      )}
    </StyledTableHeader>
  )
}
